import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Trophy, RefreshCw, ArrowRight, Loader2, Home, Building2, Mountain, DoorOpen, Share2, Flame, CheckCircle2, XCircle } from "lucide-react";

const TYPE_ICON = { appartement: Building2, maison: Home, chalet: Mountain, studio: DoorOpen };
const COND_LABEL = { neuf: "Neuf / récent", bon: "Bon état", a_rafraichir: "À rafraîchir", a_renover: "À rénover" };

const euro = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n)) + " €";

const TOTAL_ROUNDS = 5;

export const JustePrixGame = ({ embedded = false }) => {
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [lastPoints, setLastPoints] = useState(0);
  const [roundNumber, setRoundNumber] = useState(1);
  const [correctCount, setCorrectCount] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const loadRound = useCallback(async () => {
    setLoading(true);
    setRevealed(false);
    setSelected(null);
    try {
      const { data } = await api.get("/game/round");
      setRound(data);
    } catch (e) {
      setRound(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRound(); }, [loadRound]);

  const choose = (opt) => {
    if (revealed) return;
    setSelected(opt);
    const correct = opt === round.price;
    if (correct) {
      const bonus = streak * 50;
      const pts = 100 + bonus;
      const newStreak = streak + 1;
      setLastPoints(pts);
      setScore((s) => s + pts);
      setStreak(newStreak);
      setBestStreak((b) => Math.max(b, newStreak));
      setCorrectCount((c) => c + 1);
    } else {
      setLastPoints(0);
      setStreak(0);
    }
    setRevealed(true);
  };

  const nextRound = () => {
    if (roundNumber >= TOTAL_ROUNDS) {
      setGameOver(true);
      return;
    }
    setRoundNumber((n) => n + 1);
    loadRound();
  };

  const restartGame = () => {
    setScore(0);
    setStreak(0);
    setBestStreak(0);
    setCorrectCount(0);
    setLastPoints(0);
    setRoundNumber(1);
    setGameOver(false);
    loadRound();
  };

  const shareGame = async () => {
    const url = window.location.origin + "/jeu";
    const text = `🏠 J'ai marqué ${score} points au Juste Prix Immo (${correctCount}/${TOTAL_ROUNDS} bonnes réponses) ! Sauras-tu faire mieux et deviner la valeur des biens en Auvergne-Rhône-Alpes ?`;
    if (navigator.share) {
      try { await navigator.share({ title: "Le Juste Prix Immo", text, url }); } catch (e) {}
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
    }
  };

  const isCorrect = selected !== null && selected === round?.price;
  const isLastRound = roundNumber >= TOTAL_ROUNDS;
  const Icon = round ? (TYPE_ICON[round.property.property_type] || Home) : Home;

  return (
    <div className="max-w-2xl mx-auto" data-testid="juste-prix-game">
      {/* Score bar */}
      <div className="flex items-center justify-between mb-4 px-1">
        <div className="flex items-center gap-2 text-slate-700">
          <Trophy className="w-5 h-5 text-[#C85A40]" />
          <span className="font-display font-extrabold text-xl" data-testid="game-score">{score}</span>
          <span className="text-sm text-slate-400">pts</span>
        </div>
        {!gameOver && (
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500" data-testid="game-round-indicator">
            Manche {roundNumber} / {TOTAL_ROUNDS}
          </div>
        )}
        {!gameOver && streak > 1 && (
          <div className="flex items-center gap-1.5 text-sm font-semibold text-[#C85A40]" data-testid="game-streak">
            <Flame className="w-4 h-4" /> Série de {streak}
          </div>
        )}
      </div>

      {gameOver ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white border border-slate-200 rounded-3xl shadow-sm p-8 sm:p-10 text-center"
          data-testid="game-over-screen"
        >
          <div className="w-16 h-16 mx-auto rounded-2xl bg-orange-50 flex items-center justify-center mb-5">
            <Trophy className="w-8 h-8 text-[#C85A40]" />
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-2">Partie terminée</div>
          <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Votre score</h3>
          <div className="my-6">
            <div className="font-display text-6xl font-extrabold text-[#C85A40]" data-testid="game-final-score">{score}</div>
            <div className="text-slate-400 text-sm mt-1">points</div>
          </div>
          <div className="grid grid-cols-2 gap-3 max-w-sm mx-auto mb-8">
            <div className="rounded-2xl bg-slate-50 border border-slate-100 py-4">
              <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="game-correct-count">{correctCount}/{TOTAL_ROUNDS}</div>
              <div className="text-xs text-slate-500 mt-0.5">bonnes réponses</div>
            </div>
            <div className="rounded-2xl bg-slate-50 border border-slate-100 py-4">
              <div className="font-display text-2xl font-extrabold text-slate-900 flex items-center justify-center gap-1" data-testid="game-best-streak">
                <Flame className="w-5 h-5 text-[#C85A40]" />{bestStreak}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">meilleure série</div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={restartGame}
              data-testid="game-restart-btn"
              className="flex-1 flex items-center justify-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-900 px-5 py-3 rounded-lg font-semibold transition-all"
            >
              <RefreshCw className="w-4 h-4" /> Rejouer une partie
            </button>
            <a
              href={embedded ? "#estimation" : "/#estimation"}
              data-testid="game-estimate-btn"
              className="flex-1 flex items-center justify-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 px-5 py-3 rounded-lg font-semibold transition-all"
            >
              Estimer mon bien <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <button
            onClick={shareGame}
            data-testid="game-share-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 text-[#C85A40] hover:bg-orange-50 px-5 py-3 rounded-lg font-semibold transition-all"
          >
            <Share2 className="w-4 h-4" /> Défie tes amis
          </button>
        </motion.div>
      ) : (
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
        {loading || !round ? (
          <div className="h-96 flex items-center justify-center">
            {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" /> : <span className="text-slate-400">Impossible de charger le jeu.</span>}
          </div>
        ) : (
          <>
            <div className="relative h-60 sm:h-72 overflow-hidden">
              <img src={round.image} alt="Bien à estimer" className="w-full h-full object-cover" data-testid="game-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3 text-white">
                <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
                  <Icon className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-display font-bold text-lg capitalize leading-tight" data-testid="game-property-label">
                    {round.property.property_type} · {round.property.surface} m²
                  </div>
                  <div className="text-sm text-slate-200">{round.property.city}</div>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap gap-2 mb-6 text-xs">
                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600">{round.property.rooms} pièces</span>
                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600">{round.property.bedrooms} chambre{round.property.bedrooms > 1 ? "s" : ""}</span>
                <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-600">{COND_LABEL[round.property.condition]}</span>
                {round.property.exterior.map((e) => (
                  <span key={e} className="px-3 py-1 rounded-full bg-blue-50 text-[#2563EB] capitalize">{e}</span>
                ))}
              </div>

              <label className="block text-sm font-medium text-slate-700 mb-3">Quel est le juste prix de ce bien ?</label>
              <div className="grid gap-3" data-testid="game-options">
                {round.options.map((opt, idx) => {
                  const isThis = selected === opt;
                  const isAnswer = opt === round.price;
                  let cls = "border-slate-200 hover:border-[#2563EB] hover:bg-blue-50/50 text-slate-800";
                  if (revealed && isAnswer) cls = "border-emerald-500 bg-emerald-50 text-emerald-700";
                  else if (revealed && isThis && !isAnswer) cls = "border-red-400 bg-red-50 text-red-600";
                  else if (revealed) cls = "border-slate-200 text-slate-400 opacity-60";
                  return (
                    <button
                      key={idx}
                      onClick={() => choose(opt)}
                      disabled={revealed}
                      data-testid={`game-option-${idx}`}
                      className={`flex items-center justify-between border-2 rounded-xl px-5 py-4 font-display font-bold text-lg transition-all ${cls}`}
                    >
                      <span>{euro(opt)}</span>
                      {revealed && isAnswer && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                      {revealed && isThis && !isAnswer && <XCircle className="w-5 h-5 text-red-500" />}
                    </button>
                  );
                })}
              </div>

              <AnimatePresence>
                {revealed && (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 text-center" data-testid="game-result">
                    {isCorrect ? (
                      <div className="mb-1">
                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                          <Trophy className="w-5 h-5" /> <span className="font-display font-bold text-xl">Bonne réponse ! 🎉</span>
                        </div>
                        <p className="text-slate-600 mt-1">
                          <b className="text-emerald-600" data-testid="game-points">+{lastPoints} points</b>
                          {streak > 1 && <span className="text-[#C85A40]"> (bonus série ×{streak - 1} !)</span>}
                        </p>
                      </div>
                    ) : (
                      <div className="mb-1">
                        <div className="flex items-center justify-center gap-2 text-red-500">
                          <XCircle className="w-5 h-5" /> <span className="font-display font-bold text-xl">Raté !</span>
                        </div>
                        <p className="text-slate-600 mt-1">
                          Le juste prix était <b className="text-slate-900" data-testid="game-real-price">{euro(round.price)}</b>.
                        </p>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 mt-5">
                      <button
                        onClick={nextRound}
                        data-testid="game-next-btn"
                        className="flex-1 flex items-center justify-center gap-2 bg-[#2563EB] text-white hover:bg-blue-700 px-5 py-3 rounded-lg font-semibold transition-all"
                      >
                        {isLastRound ? (
                          <>Voir mon score <Trophy className="w-4 h-4" /></>
                        ) : (
                          <>Manche suivante <ArrowRight className="w-4 h-4" /></>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default function GamePage() {
  return (
    <div className="App min-h-screen bg-slate-50" data-testid="game-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl mx-auto text-center mb-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#2563EB] mb-3">Le jeu</div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tighter font-extrabold text-slate-900">
            Le Juste Prix Immo
          </h1>
          <p className="mt-4 text-lg text-slate-600">
            Saurez-vous deviner la valeur de ce bien en Auvergne-Rhône-Alpes ? Testez votre flair immobilier !
          </p>
        </div>
        <JustePrixGame />
      </div>
      <Footer />
    </div>
  );
}
